import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ILinks } from '../utils/constants/routes.interface';
import {
  dimissibleLinkMap,
  dismissibleLinkPaths,
} from 'Routing/Layouts/TabbedLayout/constants/linkMap/dismissible.linkMap';
import { useGetStudioHomeRoute } from 'Routing/index';

export type TAddDismissibleLink = (link: { path: string; name: string; link: ILinks }) => void;
export interface IDismissibleLinks {
  addDismissibleLink: TAddDismissibleLink;
}

const useDismissibleLinks = () => {
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [backNavigation, setBackNavigation] = useState<string | null>(null);
  const [dismissibleLinks, setDismissibleLinks] = useState<Map<string, ILinks>>(new Map());
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const StudioPath = useGetStudioHomeRoute();

  const updateBackNavigation = useCallback(() => {
    // Check if the current path is a dismissible link
    if (!dismissibleLinkPaths.includes(pathname)) {
      setBackNavigation(pathname);
    }
  }, [pathname]);

  const removeLink = useCallback(
    ({ path, name }: { path: string; name: string }) => {
      const linkKey = `/${path}/${name}`.toLowerCase();
      const dismissibleLinksCopy = new Map(dismissibleLinks);
      if (!dismissibleLinksCopy.has(linkKey)) return;
      dismissibleLinksCopy.delete(linkKey);
      setDismissibleLinks(dismissibleLinksCopy);
    },
    [dismissibleLinks]
  );

  const addDismissibleLink = useCallback(
    ({ path, name, link }: { path: string; name: string; link: ILinks }) => {
      const linkKey = `/${path}/${name}`.toLowerCase();
      // Check if the link is already a part of the dismissible links
      if (dismissibleLinks.has(linkKey)) {
        navigate(linkKey);
        return;
      }

      const dismissibleLinksCopy = new Map(dismissibleLinks);
      dismissibleLinksCopy.set(linkKey, link);
      setDismissibleLinks(dismissibleLinksCopy);
      updateBackNavigation();
      // Set pending navigation to the new link
      setPendingNavigation(linkKey);
    },
    [dismissibleLinks, updateBackNavigation, navigate]
  );

  const removeDismissibleLink = useCallback(
    (path: string, name: string) => {
      const isCurrentPath = pathname && pathname.includes(`/${path}/${name}`.toLowerCase());
      if (isCurrentPath) {
        navigate(backNavigation || StudioPath);
        setTimeout(() => {
          removeLink({ path, name });
        }, 300);
      } else {
        removeLink({ path, name });
      }
    },
    [pathname, navigate, removeLink, StudioPath, backNavigation]
  );

  useEffect(() => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigate]);

  useEffect(() => {
    // update the back navigation if we have a dismissible link active
    if (dismissibleLinks.size > 0 && pathname) {
      updateBackNavigation();
    }
  }, [pathname, dismissibleLinks.size, updateBackNavigation]);

  useEffect(() => {
    if (pathname && dismissibleLinkPaths.includes(pathname) && !dismissibleLinks.get(pathname)) {
      const path = pathname.split('/')?.[1]?.toLowerCase() || '';
      const label = pathname.split('/')?.[2]?.toLowerCase() || '';
      addDismissibleLink({
        path,
        name: label,
        link: dimissibleLinkMap[label],
      });
    }
  }, [pathname, dismissibleLinks, addDismissibleLink]);

  return {
    dismissibleLinks,
    addDismissibleLink,
    removeDismissibleLink,
  };
};

export default useDismissibleLinks;
